import { useEffect, useRef, useState } from "react";
import NotFoundException from "@/exceptions/NotFoundException.ts";
import { resolveGltfPath } from "@/features/model-renderer/utils/resolver.ts";
import useCursorDrag from "@/hooks/use-cursor-drag.ts";
import { useAppSelector } from "@/redux/store.ts";
import type { Nullable } from "@/types";
import Experience from "../experience/Experience.ts";
import fixupModel from "../utils/fixup-model.ts";
import CustomControls from "./custom-controls.tsx";
import TechnicalDrawing from "./technical-drawing.tsx";

export default function ModelRenderer() {
    const ref = useRef<HTMLCanvasElement>(null);
    const experience = useRef<Nullable<Experience>>(null);

    const [ready, setReady] = useState<boolean>(false);

    const gltfPath = useAppSelector((state) => state.model.gltfPath);
    const smartNumberMapping = useAppSelector((state) => state.smartNumber.mapping);
    const parts = useAppSelector((state) => state.parts.items);

    useCursorDrag(ref);

    const zoomIn = () => experience.current?.zoomIn();
    const zoomOut = () => experience.current?.zoomOut();

    useEffect(() => {
        const path = resolveGltfPath(gltfPath);
        if (!path) return undefined;

        const canvas = ref.current;
        if (!canvas) throw new NotFoundException("<canvas />");

        experience.current = new Experience(
            canvas,
            path,
            fixupModel(() => setReady(true)),
        );

        return () => {
            if (!experience.current) return;

            experience.current.destroy();
            experience.current = null;

            setReady(false);
        };
    }, [gltfPath]);

    useEffect(() => {
        if (!experience.current || !ready) return;

        void experience.current.debugModel(parts);
    }, [ready, parts]);

    useEffect(() => {
        if (!experience.current || !ready || !smartNumberMapping) return;

        void experience.current.updateModel(smartNumberMapping);
    }, [ready, smartNumberMapping]);

    return (
        <div className="model-renderer">
            <canvas ref={ref} className="model-renderer__canvas" />
            {ready && (
                <>
                    <CustomControls onZoomIn={zoomIn} onZoomOut={zoomOut} />
                    <TechnicalDrawing />
                </>
            )}
        </div>
    );
}
